import { AfterViewInit, Component, OnInit, Output, EventEmitter, ElementRef, ViewChild, Input, HostListener, TemplateRef, OnChanges } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { faSquareQuestion } from '@fortawesome/pro-solid-svg-icons';

import { ProductBarcode } from '../services/product-barcode';
import { InventoryService } from '../services/inventory.service';
import { FormatHelperService } from '../../shared/helpers/format-helper.service';

@UntilDestroy()
@Component({
    selector: 'pcg-scan-product',
    templateUrl: './scan-product.component.html',
    styleUrls: ['./scan-product.component.scss'],
    standalone: false
})
export class ScanProductComponent implements OnInit, AfterViewInit, OnChanges {

	@ViewChild('helpModal', { static: true }) helpModal!: TemplateRef<any>;
	@ViewChild('txtBarcode') txtBarcode!: ElementRef;

	@Input() autofocus = false;
	@Input() includeProductInfo = false;
	@Input() inputPlaceholder = 'Click to Start Scanning';
	@Input() ndc!: string;
	@Input() isDisabled = false;
	@Input() isGs1 = true;
	@Input() scanDetection = false; // When enabled, this will detect any scans on the the page.
	@Input() showHelp = true;
	
	@Output() scanSuccess = new EventEmitter<ProductBarcode>();
	@Output() scanError = new EventEmitter<ProductBarcode>();
	@Output() scan = new EventEmitter<ProductBarcode>();	

	// These variables are used for the scanning sounds
	beepSuccess = new Audio();
	beepError = new Audio();

	isControlDown = false;
	isProcessing = false;

	faIcon = { faSquareQuestion };

	// These variables are used for scan detection.
	private timeoutId!: ReturnType<typeof setTimeout> | undefined;
	private _input: string = '';
	private _oldValue: string = '';

	/** Handle control up and down */
	@HostListener('document:keydown', ['$event']) controlDown(event: KeyboardEvent) { 
		if (event.key === 'Control') { this.isControlDown = true; } 
		// If scan detection has been enabled.
		if (this.scanDetection) { this.scanDetectionProcessing(event); }
	}

	@HostListener('document:keyup', ['$event'])	controlUp(event: KeyboardEvent) { if (event.key === 'Control') { this.isControlDown = false; } }

	constructor(
		private inventoryService: InventoryService
		, public modalService: NgbModal
	) { }

	ngOnInit() {
		const basePath = document.getElementsByTagName('base')[0].href;
		this.beepSuccess.src = basePath + 'assets/sounds/GreenScan.mp3';
		this.beepSuccess.load();
		this.beepError.src = basePath + 'assets/sounds/RedScan.mp3';
		this.beepError.load();
	}

	ngAfterViewInit() { if (this.autofocus) { this.focus(); } }

	ngOnChanges() { if (this.autofocus) { this.focus(); } }
	focus() { setTimeout(() => { this.txtBarcode?.nativeElement?.focus(); }, 50); }

	/**
	 * Hitting F8 and Ctrl + ] will add a double arrow, which can be used as a delimiter.
	 * Hitting Enter will process the barcode.
	 *
	 * @param event The key down event
	 */
	keyDown(event: any) {
		if (
			event.key === 'F8' 
			|| (
				this.isControlDown 
				&& event.key === ']'
			)
			|| event.key === 'Alt'
		) { this.txtBarcode.nativeElement.value += '↔'; } 
		else if (event.key === 'Enter') {
			event.preventDefault();
			this.scanBarcode();
		}
	}

	/** This function gets fired when enter is pressed in the textbox or the scan button is pressed */
	scanBarcode() {
		this.isProcessing = true; // Disable scan input.
		try {
			if (
				!FormatHelperService.GetIsNullyOrWhitespace(this.txtBarcode?.nativeElement?.value) 
				&& this.isGs1 === true
			) {
				const barcodeStr = this.txtBarcode?.nativeElement?.value;
				this.txtBarcode.nativeElement.value = '';
				// Get the barcode info
				let barcode: ProductBarcode = null!;
				// Standard barcode, can only get the NDC
				if (barcodeStr?.length === 12) {
					barcode = {
						ndc10: this.inventoryService.parseNdc10FromGtin(barcodeStr)!
						, lotNumber: null!
						, serialNumber: null!
						, expirationDate: null!
					};
				} else {
					// Fancy GS1 barcode, can get all data
					barcode = this.inventoryService.scanGs1Barcode(barcodeStr)!;
				}
				this.emitScan(barcode);
			} else if (this.isGs1 === false) {
				// Linear barcode so just keep going
			}
			this.isProcessing = false; // ensure scan is re-enabled.
		} catch (error) {
			this.isProcessing = false; // ensure scan is re-enabled.
			throw error;
		}
	}

	/**
	 * This function emits the scan events that can be used by other components
	 *
	 * @param barcode The ProductBarcode that was parsed
	 */
	emitScan(barcode: ProductBarcode) {
		this.isProcessing = false; // ensure scan is re-enabled.
		if (barcode !== null) {
			this.beepSuccess.play();
			this.scanSuccess.emit(barcode);
		} else {
			this.beepError.play();
			this.scanError.emit(barcode);
		}

		this.scan.emit(barcode);
	}

	scanDetectionProcessing(event: KeyboardEvent) {		
		let target = (event.target as HTMLFormElement);
		// When focused in the scanner, return to speed things up.
		if (target.id === this.txtBarcode?.nativeElement?.id) { return; }
		// Since the scan happens so rapidly, timeoutId having value should indicate a scan is in progress.
		if (this.timeoutId) {
			// Prevent the timeout function below from happening. timeoutId will be the for the last time this was fired in the last 60 milliseconds.
			clearTimeout(this.timeoutId);
			// Getting here means that a scan is most likey in progress. If the user is currently focused on another input element, we want to retain the elements original value. 
			target.value = this._oldValue;
		}
		// Save the inputs original value. 
		else { this._oldValue = target?.value; }
		// The following should only process on manual inputs, or when the last character of a scan has been entered.
		this.timeoutId = setTimeout(() => {
			// reset these variables after manual entry or the last character of a scan has been entered.
			this._input = '';
			this.timeoutId = null!;
			// On 'Enter' trigger the key down, validation in this function will take over from here.
			if (event.key === 'Enter') { this.keyDown(event); }
		}, 30); // May need to up the timeout if the browser cannot process quickly enough.
		// The last character on a scan should be 'Enter', once input set the scan value.
		if (event.key === 'Enter') { this.txtBarcode.nativeElement.value = this._input; }
		// Capture delimeter appropriately.
		else if (
			event.key === 'F8' 
			|| (
				this.isControlDown 
				&& event.key === ']'
			) 
			|| event.key === 'Alt'
		) { this._input += '↔'; }
		// Ignore complex keys.
		else if (event.key.length > 1) { return; }
		// Adding the event key to the '_input' variable here since scans should finish procesessing before the value is reset in the timeout function above.
		else { this._input += event.key; }
	}

	helpButtonClicked() { this.modalService.open(this.helpModal); }
}
